"use client"

import dynamic from "next/dynamic"
import { useEffect, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { BarberChair } from "@/components/floor-plan/barber-chair"
import { BoothMark } from "@/components/ui/booth-mark"
import { PulseDot } from "@/components/ui/pulse-dot"

// The guest pass card, on the landing page.
//
// Not a picture of the product — the product. Same black face, same ear, same
// chair, same floor (price left, action right) as the card a barber actually
// taps on a shop's listing, down to the 236x272 frame and the fillets that cut
// the ear into the hero. Someone who clicks this and then opens a listing
// should recognise the thing they already touched.
//
// Two differences, both deliberate:
//   - the drawn chair, not the WebGL one. A marketing page should not pull a
//     3D chunk to show a 70px object.
//   - "reserve" asks for nothing. It turns the card over and says what would
//     happen next, which is the honest version of a demo: no form, no account,
//     nothing pretending to have been booked.
//
// The flip is the listing's own: perspective on the frame, preserve-3d on the
// rotating wrapper, two absolutely-positioned faces with their backfaces
// hidden. Explicit controls, never hover — a card that turns when the cursor
// arrives swings the control away from the pointer reaching for it.

// The same chair the owner's floor plan and every listing draw — the real
// object, not a picture of one. Loaded on demand so the WebGL chunk stays out
// of the landing page's bundle, with the drawn chair standing in until it
// arrives and if WebGL never starts.
const BoothChair3D = dynamic(
  () => import("@/components/floor-plan/booth-chair-3d").then((m) => m.BoothChair3D),
  { ssr: false, loading: () => null },
)

const EAR_FILLET = {
  width: 12,
  height: 12,
  background: "radial-gradient(circle at 100% 100%, transparent 11.75px, #0a0a0a 12.25px)",
} as const

const CHAIR_W = 70
const CHAIR_H = 89

type Face = "front" | "details" | "asked"

export function GuestPassCard({ audience }: { audience: "owner" | "barber" }) {
  const t = useTranslations("marketing.landing.guestPass.card")
  const [face, setFace] = useState<Face>("front")
  const flipped = face !== "front"
  // The chair turns once when the card first appears, and again whenever a
  // cursor arrives — the listing's own flourish (.chair-spin), not a new one.
  // A perpetual turn would show the canvas edge-on for half of every cycle;
  // this is a 900ms flick of the object, which is what reads as alive.
  const [spinning, setSpinning] = useState(false)
  const [chairFailed, setChairFailed] = useState(false)
  const introSpun = useRef(false)

  // The owner/barber switch changes what the back says, so a card left turned
  // over would silently swap its sentence behind your back. Start it over.
  useEffect(() => setFace("front"), [audience])

  useEffect(() => {
    if (introSpun.current) return
    introSpun.current = true
    const t = window.setTimeout(() => setSpinning(true), 400)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <div className="relative mx-auto" style={{ perspective: 1200, height: 272, width: 236 }}>
      <div
        className="relative h-full w-full transition-transform duration-500 ease-out motion-reduce:transition-none"
        style={{ transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
      >
        {/* FRONT — the offer */}
        <div
          className="floor-card absolute inset-0 flex flex-col gap-2 overflow-hidden bg-neutral-950 p-2 pb-2.5 text-left text-white shadow-[0_1px_2px_rgba(0,0,0,0.22)]"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
          aria-hidden={flipped}
        >
          {/* The ear: a tab of the card's own surface cut into the hero's
              corner, so the label reads as part of the card and not a sticker
              stuck on it. */}
          <div
            className="absolute left-0 top-0 z-10 inline-flex items-center gap-1 rounded-br-[10px] bg-neutral-950 pl-4 pr-2.5 pt-1.5 text-[9px] font-semibold uppercase tracking-wider text-white/90"
            style={{
              height: 30,
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              opacity: flipped ? 0 : 1,
              transition: "opacity 150ms ease-out",
            }}
          >
            <BoothMark size={13} className="relative -top-px shrink-0 overflow-visible" />
            {t("label")}
            <span aria-hidden className="absolute" style={{ ...EAR_FILLET, left: "calc(100% - 1px)", top: 8, width: 13 }} />
            <span aria-hidden className="absolute" style={{ ...EAR_FILLET, top: "calc(100% - 1px)", left: 8, height: 13 }} />
          </div>

          <div
            className="floor-card-hero floor-card-hero-box chair-spin-stage flex w-full items-center justify-center bg-white text-white"
            style={{ height: 140 }}
            onMouseEnter={() => setSpinning(true)}
          >
            <div
              className={spinning ? "chair-spin" : undefined}
              onAnimationEnd={() => setSpinning(false)}
              style={{ width: CHAIR_W, height: CHAIR_H }}
            >
              {chairFailed ? (
                <BarberChair pct={null} />
              ) : (
                <BoothChair3D pct={null} onUnavailable={() => setChairFailed(true)} />
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col px-0.5">
            <div className="flex min-w-0 items-center gap-1.5">
              <PulseDot />
              <p className="truncate text-[13px] font-medium text-white">{t("name")}</p>
            </div>
            <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-white/55">{t("desc")}</p>
            <div className="min-w-0" style={{ marginTop: 3 }}>
              <button
                type="button"
                onClick={() => setFace("details")}
                className="shrink-0 text-[11px] font-medium text-white/60 underline underline-offset-2 transition-colors hover:text-white"
              >
                {t("details")}
              </button>
            </div>
          </div>

          <div className="mt-auto flex min-w-0 items-baseline justify-between gap-2 pl-0.5 pt-2.5">
            <span className="text-[15px] font-normal tracking-tight text-booth-green">{t("price")}</span>
            <button
              type="button"
              onClick={() => setFace("asked")}
              className="shrink-0 text-[12px] font-medium lowercase text-white transition-opacity hover:opacity-70"
            >
              {t("reserve")} +
            </button>
          </div>
        </div>

        {/* BACK — what the front just promised, said plainly */}
        <div
          className="floor-card absolute inset-0 flex flex-col overflow-hidden bg-neutral-950 p-4 text-left text-white shadow-[0_1px_2px_rgba(0,0,0,0.22)]"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
          aria-hidden={!flipped}
        >
          <BoothMark size={18} className="shrink-0 text-white/35" />
          <p className="mt-3 text-[15px] font-medium leading-snug">
            {face === "asked" ? t("asked.heading") : t(`detail.${audience}.heading`)}
          </p>
          <p className="mt-2 text-[12px] leading-relaxed text-white/55">
            {face === "asked" ? t(`asked.${audience}.body`) : t(`detail.${audience}.body`)}
          </p>
          <button
            type="button"
            onClick={() => setFace("front")}
            className="mt-auto self-start text-[11px] font-medium text-white/60 underline underline-offset-2 transition-colors hover:text-white"
          >
            {t("back")}
          </button>
        </div>
      </div>
    </div>
  )
}
